// "Background chosen per pose" all-pose digital downloads (ENG-3723).
//
// An all-pose download delivers every pose in the student's gallery and has no
// image nodes, so the parent never picks a pose. With the product's
// `background_per_pose` switch on (CompanyPicsProduct#chooses_background_per_pose?,
// serialized by _pricing_model_product.json.jbuilder) the parent instead picks
// ONE background for EACH pose, and each pose is delivered on its own.
//
// The single-product screen already pairs pose slot i with background slot i:
// the picker composites slot i's pose over every background, stores the pick at
// index i, the gate requires every slot, and the cart sends poseIds[i] with
// backgroundIds[i], which checkout stores as one OrderItemImage per pose
// (BuildOrderFromCart) and DigitalDownloads::PerPoseBackgrounds builds from.
// So rather than a second picker, the product is configured as an N-slot product
// whose N pose slots are FIXED to the gallery's poses: `perPoseConfiguredProduct`
// below. `poses_fixed` hides the pose step (poseConfigurationRequiredSelector)
// and turns a pose tile tap into "choose this pose's background" (PoseSlot).
//
// Post-event only. On a prepay order there are no poses yet, so the product is
// configured exactly as it is served — today's all-pose selected_background,
// whose Backgrounds Count choices apply to every pose.

// The product fields these helpers read. Every serialized product carries
// them (_pricing_model_product.json.jbuilder); the rest of a product passes
// through perPoseConfiguredProduct untouched.
export interface PerPoseProduct {
  background_per_pose?: boolean | null;
  is_digital_download?: boolean | null;
  digital_download_background_type?: string | null;
  poses_fixed?: boolean | null;
}

export interface PerPosePose {
  id: number;
  url: string;
  [key: string]: unknown;
}

// One individual pose slot (the shape imageRecords reads from image_nodes).
export interface PerPoseImageNode {
  label: string;
  image_type: 'portrait_image';
}

// What perPoseConfiguredProduct adds to (or overrides on) the product.
export interface PerPoseConfiguration {
  image_nodes: PerPoseImageNode[];
  background_options_count: number;
  crop_options_enabled: false;
  poses_fixed: true;
}

export type PerPoseConfiguredProduct<P extends PerPoseProduct> = Omit<
  P,
  keyof PerPoseConfiguration
> &
  PerPoseConfiguration;

// Mirrors the server's rule: the switch only means something on a
// selected_background digital download (the payload already reports false
// otherwise; this keeps a stale persisted product from turning it on).
export const productChoosesBackgroundPerPose = (
  product: PerPoseProduct | null | undefined,
): boolean =>
  Boolean(
    product?.background_per_pose &&
      product?.is_digital_download &&
      product?.digital_download_background_type === 'selected_background',
  );

// Whether the product's pose slots are fixed (configured by
// perPoseConfiguredProduct): the parent picks backgrounds only.
export const productPosesFixed = (
  product: PerPoseProduct | null | undefined,
): boolean => Boolean(product?.poses_fixed);

// The product as the single-product screen configures it: one individual pose
// slot and one background slot per gallery pose, crops off (the all-pose build
// delivers each pose through its preferred crop, so a crop pick would be
// ignored), and the pose slots fixed.
export const perPoseConfiguredProduct = <P extends PerPoseProduct>(
  product: P,
  poses: PerPosePose[],
): PerPoseConfiguredProduct<P> => ({
  ...product,
  image_nodes: poses.map(
    (_pose, index): PerPoseImageNode => ({
      label: `Pose ${index + 1}`,
      image_type: 'portrait_image',
    }),
  ),
  background_options_count: poses.length,
  crop_options_enabled: false,
  poses_fixed: true,
});
